import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── A row of filter chips that never wraps (owner 2026-09-25) ────────────────
// The Legal Matters service chips sat on a row of their own, and on a narrow window they wrapped
// onto a third. The owner's rule, given twice now: one row, and whatever does not fit folds into a
// dropdown at the end — 确保都显示到一排上.
//
// Kept separate from Peak's PeakToolbar on purpose. That one folds ACTIONS, each of which may carry
// its own menu, and a folded action is simply a row in the More menu. These are MUTUALLY EXCLUSIVE
// FILTERS, where folding has an extra duty: if the chip that is currently selected is one of the
// hidden ones, the button at the end has to say so — otherwise the screen shows a filtered list
// with nothing on it explaining why. So the button wears the selected chip's label and its active
// colours whenever the selection is folded away.
//
// Measuring, not a breakpoint: the chips are service families read from the server, so their number
// and their labels differ per desk and per language. Widths come from the elements themselves — a
// folded chip stays in the DOM, parked off-screen, so it can still be measured — and the count only
// ever settles on what fits the space this row was actually given.
export type OChip = { key: string; label: string; active?: boolean; muted?: boolean; title?: string; onClick: () => void };

export default function OverflowChips({ chips, gap = 6, style, chipStyle, activeStyle, mutedColor, font }: {
  chips: OChip[];
  gap?: number;
  style?: React.CSSProperties;          // the wrapper (it must be allowed to shrink)
  chipStyle: React.CSSProperties;       // a resting chip
  activeStyle: React.CSSProperties;     // merged over chipStyle for the selected one
  mutedColor?: string;                  // colour for chips flagged muted (e.g. no price on file yet)
  font?: string;
}) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const n = chips.length;
  const [shown, setShown] = useState(n);
  const [open, setOpen] = useState(false);
  const labels = chips.map(c => c.label).join('|');

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = () => {
      const avail = wrap.clientWidth;
      const w = itemRefs.current.slice(0, n).map(el => el?.offsetWidth || 0);
      const moreW = moreRef.current?.offsetWidth || 96;
      const need = (k: number) =>
        w.slice(0, k).reduce((s, x) => s + x + gap, 0) - (k ? gap : 0) + (k < n ? gap + moreW : 0);
      let k = n;
      while (k > 0 && need(k) > avail) k--;
      setShown(prev => (prev === k ? prev : k));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [n, labels, gap]);

  const folded = chips.slice(shown);
  const foldedActive = folded.find(c => c.active);
  const off: React.CSSProperties = { position: 'absolute', left: -99999, top: 0, visibility: 'hidden', pointerEvents: 'none' };
  const render = (c: OChip) => (
    <button onClick={c.onClick} title={c.title}
      style={{ ...chipStyle, ...(c.active ? activeStyle : {}), ...(c.muted && !c.active && mutedColor ? { color: mutedColor } : {}), fontFamily: font, whiteSpace: 'nowrap' }}>
      {c.label}
    </button>
  );

  return (
    <div ref={wrapRef} style={{ display: 'flex', alignItems: 'center', gap, minWidth: 0, flex: 1, overflow: 'visible', ...style }}>
      {chips.map((c, i) => (
        <div key={c.key} ref={el => { itemRefs.current[i] = el; }} style={i < shown ? { flexShrink: 0 } : off}>{render(c)}</div>
      ))}
      {shown < n && (
        <div ref={moreRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button onClick={() => setOpen(o => !o)} title={t('eml_nav_more')}
            style={{ ...chipStyle, ...(foldedActive ? activeStyle : {}), fontFamily: font, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
            {foldedActive ? foldedActive.label : t('eml_nav_more')}
            <i className="ti ti-chevron-down" style={{ fontSize: 12 }} aria-hidden />
          </button>
          {open && (
            <>
              <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, minWidth: 220, maxHeight: '60vh', overflowY: 'auto', background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: 4 }}>
                {folded.map(c => (
                  <button key={c.key} onClick={() => { setOpen(false); c.onClick(); }} title={c.title}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderRadius: 6, background: c.active ? 'var(--bg-active)' : 'transparent', color: c.muted && !c.active ? (mutedColor || 'var(--text-tertiary)') : 'var(--text-primary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: font, whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { if (!c.active) e.currentTarget.style.background = 'var(--bg-active)'; }}
                    onMouseLeave={e => { if (!c.active) e.currentTarget.style.background = 'transparent'; }}>
                    <i className="ti ti-check" style={{ fontSize: 13, opacity: c.active ? 1 : 0 }} aria-hidden />{c.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
